import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { domToPng } from "modern-screenshot";
import type { GriddSnapshot } from "@gridd/embed/react";

import { Slide, type SlideData } from "@/components/deck/Slide";
import {
  diagramCacheVersion,
  isDiagramSvgFresh,
  peekDiagramSvg,
  subscribeDiagramSvgs,
} from "@/lib/diagramSvg";
import { isChartCanvasLoaded, loadChartCanvas } from "@/lib/chart-engine";
import { isGanttCanvasLoaded, loadGanttCanvas } from "@/lib/gantt-engine";
import { useAppSelector } from "@/store/hooks";

// Full-slide thumbnails for the "Thumbnail" nav view. A slide is more than its grid — title,
// subtitle, sticker, source, page number, layout, plus text/image windows — and its grid lives
// in a cross-origin iframe that no DOM-to-image method can read. So we capture in two stages:
//   1. the grid embed rasterizes itself (thumbnailStore.tsx → a GriddSnapshot per griddDocId);
//   2. here we render a read-only <Slide> OFFSCREEN with that snapshot substituted for the
//      iframe (Slide's `snapshots` prop swaps in an <img>), making the whole slide same-origin,
//      then rasterize it once to a cached PNG per slide.
// The nav then just shows those PNGs. Capture is sequential (one offscreen slide at a time) and
// only runs while enabled (Thumbnail view, no live stylesheet draft), so it never piles up.

// Offscreen render width. Slide scales its 1600-wide canvas to fit, so this is a 400×225 box…
const THUMB_W = 400;
// …captured at 2× for a crisp 800×450 PNG.
const CAPTURE_SCALE = 2;

const noop = () => {};

export type SlideThumb = { sig: string; url: string };
// §19.0.3 The page number travels WITH the item rather than being derived from `index`
// here: a thumbnail is captured off one slide in isolation, so this component never sees
// the deck it would have to count through.
type Item = { slide: SlideData; index: number; pageNumber: string | null };
type Target = { item: Item; sig: string };

export function OffscreenSlideCapturer({
  items,
  gridSnapshots,
  styleSheet,
  enabled,
  thumbs,
  onCaptured,
}: {
  // The active slides with their presentation index, in order.
  items: Item[];
  // griddDocId→snapshot, composited into each slide in place of the live iframe.
  gridSnapshots: Record<string, GriddSnapshot>;
  // The committed stylesheet; its identity is folded into the signature so a sheet change
  // re-captures every slide (the offscreen Slide reads the effective sheet from Redux itself).
  styleSheet: unknown;
  // Capture only when true — Thumbnail view is showing and no stylesheet draft is mid-preview.
  enabled: boolean;
  // The current cache, so we know which slides are stale (sig changed) and skip the rest.
  thumbs: Record<string, SlideThumb>;
  onCaptured: (slideId: string, thumb: SlideThumb) => void;
}) {
  // §5.3 The diagram bodies on screen, and a tick that fires when the SVG render cache
  // changes. Together they are the diagram half of "is this slide ready to capture": the
  // bodies say what the signature should be, the tick re-runs the picker when a render that
  // was not ready becomes ready.
  const diagramDocs = useAppSelector((s) => s.diagram);
  const diagramTick = useSyncExternalStore(
    subscribeDiagramSvgs,
    diagramCacheVersion,
    diagramCacheVersion,
  );

  // §26/§9.1 The gantt bodies, for the signature, and whether the widget's chunk has arrived.
  // A gantt renders live rather than from a cache, so the readiness question is not "has this
  // one rendered" but "can ANY of them render yet" — the chunk is lazy (§7.1), and a slide
  // captured while it is still in flight bakes the Suspense fallback into the cache, where
  // nothing would ever invalidate it.
  const ganttDocs = useAppSelector((s) => s.gantt);
  const [ganttReady, setGanttReady] = useState(isGanttCanvasLoaded());
  useEffect(() => {
    if (ganttReady) return;
    const needed = items.some((item) =>
      item.slide.contents.some((c) => c?.kind === "gantt"),
    );
    if (!needed) return;
    let cancelled = false;
    // Fetching it here rather than only waiting for it matters: the nav can be showing
    // thumbnails of slides none of which is currently on the canvas, so nothing else in the
    // page would ever ask for the chunk and the picker would wait forever.
    void loadGanttCanvas().then(() => {
      if (!cancelled) setGanttReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [items, ganttReady]);

  // §15/§8.6 The chart bodies and the chunk gate, on exactly the terms the gantt pair above
  // gets: a chart renders live rather than from a cache, so the readiness question is not "has
  // this one rendered" but "can ANY of them render yet". A slide captured while the Recharts
  // chunk is still in flight bakes the Suspense fallback into the thumbnail cache, where
  // nothing would ever invalidate it.
  const chartDocs = useAppSelector((s) => s.chart);
  const [chartReady, setChartReady] = useState(isChartCanvasLoaded());
  useEffect(() => {
    if (chartReady) return;
    const needed = items.some((item) =>
      item.slide.contents.some((c) => c?.kind === "chart"),
    );
    if (!needed) return;
    let cancelled = false;
    // Fetching it here rather than only waiting for it matters, for the reason the gantt block
    // above gives: the nav can be showing thumbnails of slides none of which is on the canvas,
    // so nothing else in the page would ever ask for the chunk.
    void loadChartCanvas().then(() => {
      if (!cancelled) setChartReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [items, chartReady]);

  // The slide currently mounted offscreen for capture, with the signature it was picked for.
  const [target, setTarget] = useState<Target | null>(null);
  const captureRef = useRef<HTMLDivElement>(null);

  // Stable per-object token: each new GriddSnapshot / stylesheet object gets a fresh number, so
  // folding it into the signature invalidates the thumbnail without stringifying a huge dataUrl.
  const tokenMap = useRef(new WeakMap<object, number>());
  const tokenSeq = useRef(0);
  const tokenOf = useCallback((obj: object | null | undefined): number => {
    if (!obj) return 0;
    let t = tokenMap.current.get(obj);
    if (t === undefined) {
      t = ++tokenSeq.current;
      tokenMap.current.set(obj, t);
    }
    return t;
  }, []);

  // A slide's capture signature, or null when it holds a grid whose snapshot hasn't been taken
  // yet — we wait for it rather than rasterize a slide with a blank grid.
  //
  // §21/§5.3 Diagrams need the same gate for a different reason. `exportToSvg` is ASYNC and
  // this capturer is not: it rasterizes a mounted off-screen `<Slide>`, so a diagram whose
  // SVG is not yet in the render cache would be captured as an empty box — and that empty
  // box would then be cached as the slide's thumbnail and persist until the next edit.
  // Returning null here is exactly the "wait for the grid snapshot" behaviour above, reused
  // rather than reinvented: the capture effect re-arms on every input change, and the cache
  // notifies through `diagramTick`.
  const sigFor = useCallback(
    (item: Item): string | null => {
      const gridTokens: number[] = [];
      const diagramKeys: string[] = [];
      const ganttTokens: number[] = [];
      const chartTokens: number[] = [];
      for (const c of item.slide.contents) {
        if (c?.kind === "gridd") {
          const snap = gridSnapshots[c.griddDocId];
          if (!snap) return null;
          gridTokens.push(tokenOf(snap));
        } else if (c?.kind === "gantt") {
          if (!ganttReady) return null;
          // The doc OBJECT is the identity: `ganttSlice` replaces it on every edit, so a
          // token off it invalidates the thumbnail exactly when the schedule changes — the
          // same trick the grid snapshots use, for the same reason (no stringifying).
          const doc = ganttDocs[c.ganttDocId];
          ganttTokens.push(tokenOf(doc as object | undefined));
        } else if (c?.kind === "chart") {
          if (!chartReady) return null;
          // The doc OBJECT is the identity, exactly as for a gantt: `chartSlice` replaces it
          // on every edit, so a token off it invalidates the thumbnail precisely when the data
          // or the encoding changes, with nothing stringified.
          chartTokens.push(
            tokenOf(chartDocs[c.chartDocId] as object | undefined),
          );
        } else if (c?.kind === "diagram") {
          const doc = diagramDocs[c.diagramDocId];
          // No body at all — the window renders its "drawing is missing" state, which is a
          // perfectly capturable slide. Only a body WITHOUT a current render has to wait.
          if (!doc) continue;
          if (!isDiagramSvgFresh(c.diagramDocId, doc)) return null;
          diagramKeys.push(peekDiagramSvg(c.diagramDocId)?.key ?? "");
        }
      }
      return JSON.stringify({
        s: item.slide,
        i: item.index,
        g: gridTokens,
        d: diagramKeys,
        ga: ganttTokens,
        ch: chartTokens,
        st: tokenOf(styleSheet as object | null),
      });
    },
    [
      gridSnapshots,
      diagramDocs,
      ganttDocs,
      ganttReady,
      chartDocs,
      chartReady,
      styleSheet,
      tokenOf,
    ],
  );

  // Pick the next stale slide to capture whenever nothing is in flight. Runs on any input
  // change, so a fresh grid snapshot, a finished diagram render or an edit re-arms it.
  useEffect(() => {
    if (target || !enabled) return;
    for (const item of items) {
      const sig = sigFor(item);
      if (sig === null) continue; // grid snapshot or diagram SVG not ready (§5.3)
      if (thumbs[item.slide.id]?.sig !== sig) {
        setTarget({ item, sig });
        return;
      }
    }
  }, [
    target,
    enabled,
    items,
    gridSnapshots,
    diagramTick,
    styleSheet,
    thumbs,
    sigFor,
  ]);

  // Capture the currently-mounted offscreen slide, then advance.
  useEffect(() => {
    if (!target) return;
    let cancelled = false;

    const run = async () => {
      const node = captureRef.current;
      if (!node) return;
      // Let fonts, layout and any image blocks settle before rasterizing.
      try {
        await document.fonts?.ready;
      } catch {
        /* fonts API absent or failed — proceed anyway */
      }
      await new Promise<void>((r) =>
        requestAnimationFrame(() => requestAnimationFrame(() => r())),
      );
      await Promise.all(
        Array.from(node.querySelectorAll("img")).map((img) =>
          img.complete
            ? Promise.resolve()
            : new Promise<void>((res) => {
                img.addEventListener("load", () => res(), { once: true });
                img.addEventListener("error", () => res(), { once: true });
              }),
        ),
      );
      if (cancelled) return;

      let url: string | null = null;
      try {
        url = await domToPng(node, {
          scale: CAPTURE_SCALE,
          backgroundColor: "#ffffff",
        });
      } catch (e) {
        console.warn("[slide-thumbnail]", e);
      }
      if (cancelled) return;

      // Record the signature either way so a failed capture isn't retried in a tight loop; keep
      // any previously-good image on failure. Then clear `target`, which re-arms the picker.
      onCaptured(target.item.slide.id, {
        sig: target.sig,
        url: url ?? thumbs[target.item.slide.id]?.url ?? "",
      });
      setTarget(null);
    };

    void run();
    return () => {
      cancelled = true;
    };
    // Intentionally keyed only on `target`: the capture uses the slide/sig frozen when it was
    // picked, so mid-capture prop churn must not restart it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  if (!target) return null;

  return (
    // Rendered far off-screen (not display:none — the node must lay out and paint to be
    // rasterized). Kept out of the a11y tree and pointer flow.
    <div
      aria-hidden
      style={{
        position: "fixed",
        left: -100000,
        top: 0,
        width: THUMB_W,
        pointerEvents: "none",
        zIndex: -1,
      }}
    >
      <div ref={captureRef} style={{ width: THUMB_W, background: "#ffffff" }}>
        <Slide
          slide={target.item.slide}
          index={target.item.index}
          pageNumber={target.item.pageNumber}
          onChange={noop}
          presenting
          snapshots={gridSnapshots}
        />
      </div>
    </div>
  );
}
